import type { FileRelation } from "@/models/file/file-relation.model"
import { LayoutColumn } from "@/models/user_settings/layout-column.model"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import { WindowTypes } from "@/interfaces/component/window.interface"
import { useWindowStore } from "@/store/windows.store"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import router from "@/router"
import { useFileRelationStore } from "@/store/file-relation.store"
import { LayoutController } from "@/controllers/layout-controller"
import type { Component } from "vue"

interface FileViewerColumnOptions {
  /** Create a file-viewer column when no existing column is available. */
  createColumnIfNone?: boolean
}

interface DisplayFileRelationsOptions extends FileViewerColumnOptions {
  getArray?: () => FileRelation[] // A function to get the file relations.
  onDelete?: (fileRelation: FileRelation) => void // Callback for if a fileRelation is deleted.
  allowDelete?: boolean // Show deletion option for the file relations.
}

interface DisplayContentInColumnOptions extends FileViewerColumnOptions {
  content: Component
  title: string
  props?: Record<string, unknown>
}

export class FileViewerController {
  private static getTargetFileViewerColumnForFiles(): LayoutColumn | null {
    const layoutColumnsStore = useLayoutColumnsStore()
    const fileViewers = layoutColumnsStore.layoutColumns.filter(
      (column) => column.component === LayoutColumnComponents.FILE_VIEWER
    )
    const emptyFileViewer = fileViewers.find((fileViewer) => !fileViewer.props?.modelValue)
    if (emptyFileViewer) return emptyFileViewer

    if (fileViewers.length > 0) return fileViewers[0]

    return null
  }

  private static setFileViewerProps(
    fileViewer: LayoutColumn,
    fileRelations: Array<FileRelation>,
    selectedFileRelation: FileRelation | null,
    options: DisplayFileRelationsOptions
  ) {
    fileViewer.props = {
      modelValue: fileRelations,
      select: selectedFileRelation,
      getArray: options.getArray,
      onDelete: options.onDelete,
      allowDelete: options.allowDelete
    }
  }

  /**
   * Show custom content in a file-viewer column. Existing content of the same
   * type is reused before replacing an empty or occupied file-viewer column.
   */
  public static displayContentInColumn({
    content,
    title,
    props = {},
    createColumnIfNone = false
  }: DisplayContentInColumnOptions): void {
    const layoutColumnsStore = useLayoutColumnsStore()
    const fileViewers = layoutColumnsStore.fileViewers
    const matchingFileViewer = fileViewers.find(
      (fileViewer) => fileViewer.props?.contentOverride === content
    )
    const emptyFileViewer = fileViewers.find(
      (fileViewer) => !fileViewer.props?.modelValue && !fileViewer.props?.contentOverride
    )
    const fileViewer = matchingFileViewer ?? emptyFileViewer ?? fileViewers[0]
    if (!fileViewer && !createColumnIfNone) return

    const targetFileViewer =
      fileViewer ?? new LayoutController().addColumn(LayoutColumnComponents.FILE_VIEWER)

    targetFileViewer.props ??= {}
    targetFileViewer.props.contentOverride = content
    targetFileViewer.props.contentOverrideTitle = title
    targetFileViewer.props.contentOverrideProps = props
  }

  private static displayFileRelationsInWindow(
    fileRelations: Array<FileRelation>,
    selectedFileRelation: FileRelation | null,
    options: DisplayFileRelationsOptions
  ) {
    const windowStore = useWindowStore()

    windowStore.addWindow(WindowTypes.IMAGE_VIEWER, {
      modelValue: fileRelations,
      select: selectedFileRelation,
      onDelete: options.onDelete,
      allowDelete: options.allowDelete
    })
  }

  /**
   * Display a file viewer.
   * @param fileRelations - The file relations to display.
   * @param selectedFileRelation - The selected file relation.
   * @param options - See DisplayFileRelationsOptions
   */
  public static async display(
    fileRelations: Array<FileRelation>,
    selectedFileRelation: FileRelation | null,
    options: DisplayFileRelationsOptions = {}
  ): Promise<void> {
    const layoutColumnsStore = useLayoutColumnsStore()
    const fileRelationStore = useFileRelationStore()

    fileRelationStore.setFileRelations(fileRelations)
    fileRelationStore.setSelectedFileRelation(selectedFileRelation)

    const availableFileViewer = FileViewerController.getTargetFileViewerColumnForFiles()

    if (availableFileViewer) {
      FileViewerController.setFileViewerProps(
        availableFileViewer,
        fileRelations,
        selectedFileRelation,
        options
      )
    } else {
      /* If the app column is the active column, redirect to the file viewer view. */
      if (layoutColumnsStore.activeColumnComponent === LayoutColumnComponents.APP) {
        await router.push({ name: "file-viewer" })
      } else {
        /* If no FileViewers have been registered, display the image in a window. */
        FileViewerController.displayFileRelationsInWindow(
          fileRelations,
          selectedFileRelation,
          options
        )
      }
    }
  }

  /**
   * Display a file viewer in a column.
   * @param fileRelations - The file relations to display.
   * @param selectedFileRelation - The selected file relation.
   * @param options - See DisplayFileRelationsOptions
   */
  public static async displayFileRelationsInColumn(
    fileRelations: Array<FileRelation>,
    selectedFileRelation: FileRelation | null,
    options: DisplayFileRelationsOptions = {}
  ): Promise<void> {
    const foundFileViewer = FileViewerController.getTargetFileViewerColumnForFiles()

    if (!foundFileViewer) {
      if (options.createColumnIfNone) {
        const fileViewer = new LayoutController().addColumn(LayoutColumnComponents.FILE_VIEWER)
        FileViewerController.setFileViewerProps(
          fileViewer,
          fileRelations,
          selectedFileRelation,
          options
        )
        return
      }

      FileViewerController.displayFileRelationsInWindow(
        fileRelations,
        selectedFileRelation,
        options
      )
      return
    }

    FileViewerController.setFileViewerProps(
      foundFileViewer,
      fileRelations,
      selectedFileRelation,
      options
    )
  }
}
